import { useLocalization } from '@lib/localization';
import CheckIcon from '@mui/icons-material/Check';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { type CustomFolderChildRow } from '@pages/Projects';
import {
  buttonStyles,
  candidateListStyles,
  candidateSelectedStyles,
  dialogCloseStyles,
  dialogFooterStyles,
  dialogHeaderStyles,
  dialogPaperStyles,
  roleChipStyles,
} from '@pages/Teams.styles';
import {
  type IFolder,
  useAddFolderMutation,
  useGetFoldersQuery,
  useUpdateFolderMutation,
} from '@store/api/folderEndpoints';
import { type IProject, usePatchProjectMutation } from '@store/api/projectEndpoints';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { Fragment, useMemo, useState } from 'react';
import { AngleRightIcon, ArrowLeftIcon, FolderIcon, LockIcon, PlusIcon, SearchIcon, TimesIcon } from 'react-line-awesome';
import {
  buildFolderTree,
  canCreateIn,
  canMoveBack,
  folderIdOf,
  type FolderNode,
  isWithin,
  type MoveBlock,
  moveBlockOf,
  type MoveSubject,
  ownerTeamOf,
  pathLabelOf,
  ROOT_ID,
  sharesWithin,
  trailOf,
} from './folderTree';

interface MoveDialogProps {
  open: boolean;
  onClose: () => void;
  item: CustomFolderChildRow | null;
}

interface MoveRequest {
  /** The destination folder, `null` for the root. */
  to: string | null;
  toLabel: string;
}

const subjectOf = (row: CustomFolderChildRow): MoveSubject => ({
  kind: row.type,
  id: row.id,
  name: row.name,
  parentId: folderIdOf(row.type === 'Folder' ? (row.item as IFolder).parentFolder : (row.item as IProject).parentFolder),
});

/**
 * "Move to…" for a folder or a project: browse the folder tree, pick a destination, move.
 * Every answer comes from one `GET /v1/folders`, so the dialog can say before the click
 * why a folder cannot take the item, and whether the move hands it to another team.
 *
 * There is deliberately no undo. It would only be another move back, which cannot restore
 * the shares a move to another team clears, and is refused outright in the one case that
 * needs it most — see `canMoveBack`. The dialog says what a move costs before the click.
 */
const MoveDialog = ({ open, onClose, item }: MoveDialogProps) => {
  const { t } = useLocalization();
  const dispatch = useAppDispatch();

  const [updateFolder, { isLoading: isMovingFolder }] = useUpdateFolderMutation();
  const [patchProject, { isLoading: isMovingProject }] = usePatchProjectMutation();
  const isMoving = isMovingFolder || isMovingProject;

  const subject = useMemo(() => (item ? subjectOf(item) : null), [item]);

  const handleMove = async ({ to, toLabel }: MoveRequest) => {
    if (!subject || isMoving) return;

    // The generic "updated" message is skipped in favour of one that says where it went.
    const result = subject.kind === 'Folder'
      ? await updateFolder({ _id: subject.id, name: subject.name, parentFolder: to, showSuccess: false })
      : await patchProject({ id: subject.id, parentFolder: to, showSuccess: false });

    if (result.error) return;

    onClose();
    dispatch(showSnackbar({ severity: 'success', message: t('Folder_Move_Done', [subject.name, toLabel]) }));
  };

  return (
    <Dialog
      open={open}
      onClose={isMoving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { ...dialogPaperStyles, maxWidth: 600 } } }}
    >
      {/* Mounted per opening — the Dialog drops its children once closed — so each move
          starts at the item's own folder with nothing selected. */}
      {subject && <MoveBrowser subject={subject} isMoving={isMoving} onMove={handleMove} onCancel={onClose} />}
    </Dialog>
  );
};

interface MoveBrowserProps {
  subject: MoveSubject;
  isMoving: boolean;
  onMove: (request: MoveRequest) => void;
  onCancel: () => void;
}

const BLOCK_REASON: Record<MoveBlock, string> = {
  self: 'Folder_Move_Block_Self',
  inside: 'Folder_Move_Block_Inside',
  current: 'Folder_Move_CurrentLocation',
  permission: 'Folder_Move_Block_Permission',
  nameTaken: 'Folder_Move_Block_NameTaken',
};

const BLOCK_HINT: Record<MoveBlock, string> = {
  self: 'Folder_Move_Hint_Inside',
  inside: 'Folder_Move_Hint_Inside',
  current: 'Folder_Move_Hint_Current',
  permission: 'Folder_Move_Hint_Permission',
  nameTaken: 'Folder_Move_Hint_NameTaken',
};

// The same characters FolderModal and the backend refuse in a folder name.
const INVALID_NAME = /[\\/:*?"<>|]/;

// Past this many levels the breadcrumb keeps the root and the last two folders.
const MAX_CRUMBS = 4;

const MoveBrowser = ({ subject, isMoving, onMove, onCancel }: MoveBrowserProps) => {
  const { t, currentLanguage } = useLocalization();

  const { data: folders, isLoading } = useGetFoldersQuery();
  const [addFolder, { isLoading: isAdding }] = useAddFolderMutation();

  const tree = useMemo(() => buildFolderTree(folders ?? []), [folders]);

  const [cwdId, setCwdId] = useState(subject.parentId ?? ROOT_ID);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A folder created here has no id until the list refetches, so it is found by its path.
  const [createdPath, setCreatedPath] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  const rootLabel = t('Common_Projects');
  const labelOf = (node: FolderNode) => (node === tree.root ? rootLabel : node.folder.name);

  const cwd = tree.byId.get(cwdId) ?? tree.root;
  const selected = selectedId
    ? tree.byId.get(selectedId) ?? null
    : createdPath ? Array.from(tree.byId.values()).find((node) => node.folder.path === createdPath) ?? null : null;

  // With nothing selected the dialog moves into the folder being browsed.
  const target = selected ?? cwd;
  const block = moveBlockOf(target, subject);

  const lower = (text: string) => text.toLocaleLowerCase(currentLanguage);
  const needle = lower(query.trim());
  const searching = needle.length > 0;

  const nodes = searching
    ? Array.from(tree.byId.values())
      .filter((node) => node !== tree.root && lower(node.folder.name).includes(needle))
      .sort((a, b) => pathLabelOf(a, rootLabel).localeCompare(pathLabelOf(b, rootLabel)))
    : cwd.children;

  const crumbs: (FolderNode | null)[] = [tree.root, ...trailOf(cwd)];
  const shownCrumbs = crumbs.length > MAX_CRUMBS ? [tree.root, null, ...crumbs.slice(-2)] : crumbs;

  const fromTeam = ownerTeamOf(tree, subject);
  const toTeam = target.folder.ownerTeamId;
  const ownerChanges = !isLoading && !block && fromTeam !== toTeam;
  const sharesLost = ownerChanges && subject.kind === 'Folder' ? sharesWithin(tree.byId.get(subject.id)) : 0;
  // A move the reader could not reverse themselves, whatever else it changes.
  const irreversible = !isLoading && !block && !canMoveBack(tree, subject);
  const teamLabel = (teamId?: string) =>
    teamId ? tree.teamNames.get(teamId) ?? t('Folder_Move_OtherTeam') : t('Folder_Move_NoTeam');
  const ownerChangeLine = `${t('Folder_Move_OwnerChange_Title')}: ${teamLabel(fromTeam)} → ${teamLabel(toTeam)}`;

  const canCreate = !searching && !isLoading && canCreateIn(cwd, subject);

  const browse = (node: FolderNode) => {
    setCwdId(node.id);
    setSelectedId(null);
    setCreatedPath(null);
    setQuery('');
    setDraft(null);
  };

  const toggle = (node: FolderNode) => {
    setSelectedId(selected?.id === node.id ? null : node.id);
    setCreatedPath(null);
  };

  const startDraft = () => {
    setDraft('');
    setDraftError(null);
    setSelectedId(null);
    setCreatedPath(null);
  };

  const cancelDraft = () => {
    setDraft(null);
    setDraftError(null);
  };

  const submitDraft = async () => {
    if (draft === null || isAdding) return;

    const name = draft.trim();

    let error: string | null = null;
    if (!name) error = t('Folder_New_NameRequired');
    else if (INVALID_NAME.test(name)) error = t('Folder_New_InvalidChars');
    else if (cwd.children.some((child) => child.folder.name === name)) error = t('Folder_New_NameTaken');

    if (error) {
      setDraftError(error);
      return;
    }

    const result = await addFolder({ data: { name, parentFolder: cwd.id === ROOT_ID ? null : cwd.id } });

    if (result.error) return;

    setDraft(null);
    setCreatedPath(cwd.id === ROOT_ID ? `/${name}` : `${cwd.folder.path}/${name}`);
  };

  const submit = () => {
    if (block || isMoving || isLoading) return;

    onMove({ to: target.id === ROOT_ID ? null : target.id, toLabel: pathLabelOf(target, rootLabel) });
  };

  const contentsOf = (node: FolderNode) => {
    const parts = [
      node.children.length ? t('Folder_Move_Subfolders', { count: node.children.length }) : null,
      node.projectCount ? t('Folder_Move_Projects', { count: node.projectCount }) : null,
    ].filter(Boolean);

    return parts.length ? parts.join(' · ') : t('Folder_Move_Empty');
  };

  // Highlights the match only when lower-casing kept the name's length, since the offsets
  // come from the lower-cased copy.
  const highlighted = (name: string) => {
    const at = searching && lower(name).length === name.length ? lower(name).indexOf(needle) : -1;

    if (at < 0) return name;

    return (
      <>
        {name.slice(0, at)}
        <Box component="mark" sx={markStyles}>{name.slice(at, at + needle.length)}</Box>
        {name.slice(at + needle.length)}
      </>
    );
  };

  const renderRow = (node: FolderNode) => {
    const rowBlock = moveBlockOf(node, subject);
    const selectable = !rowBlock || rowBlock === 'current';
    const isSelected = selected?.id === node.id;
    const enterable = node.children.length > 0 && !(subject.kind === 'Folder' && isWithin(node, subject.id));
    const isChip = rowBlock === 'current' || rowBlock === 'self';

    return (
      <Stack
        key={node.id}
        direction="row"
        alignItems="center"
        sx={isSelected ? [rowStyles, candidateSelectedStyles] : rowStyles}
      >
        <ButtonBase
          disabled={!selectable}
          aria-pressed={isSelected}
          onClick={() => toggle(node)}
          onDoubleClick={() => enterable && browse(node)}
          sx={rowButtonStyles}
        >
          <Box sx={{ display: 'flex', color: isSelected ? 'primary.light' : 'text.secondary', opacity: selectable ? 1 : 0.45 }}>
            <FolderIcon style={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontWeight: 600, fontSize: 14, color: selectable ? 'text.primary' : 'text.secondary' }}
              >
                {highlighted(node.folder.name)}
              </Typography>
              {node.isTeamRoot && <Chip size="small" label={t('Folder_Team_Chip')} sx={roleChipStyles} />}
              {node.isShared && <Chip size="small" label={t('Folder_Shared_Chip')} sx={roleChipStyles} />}
              {isChip && <Chip size="small" label={t(BLOCK_REASON[rowBlock])} sx={[roleChipStyles, locationChipStyles]} />}
            </Stack>
            <Typography noWrap color="text.secondary" sx={{ fontSize: 13 }}>
              {searching ? pathLabelOf(node.parent ?? tree.root, rootLabel) : contentsOf(node)}
            </Typography>
          </Box>
          {rowBlock && !isChip && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={reasonStyles}>
              {rowBlock === 'permission' && <LockIcon style={{ fontSize: 15 }} />}
              <span>{t(BLOCK_REASON[rowBlock])}</span>
            </Stack>
          )}
          {isSelected && <CheckIcon sx={{ fontSize: 18, color: 'primary.light', flexShrink: 0 }} />}
        </ButtonBase>
        {enterable
          ? (
            <Tooltip title={t('Folder_Move_Open')}>
              <IconButton size="small" aria-label={t('Folder_Move_Open')} onClick={() => browse(node)} sx={enterStyles}>
                <AngleRightIcon />
              </IconButton>
            </Tooltip>
          )
          : <Box sx={{ width: 34, flexShrink: 0 }} />}
      </Stack>
    );
  };

  return (
    <>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={dialogHeaderStyles}>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
            {t(subject.kind === 'Folder' ? 'Folder_Move_TitleFolder' : 'Folder_Move_TitleProject', [subject.name])}
          </Typography>
          <Typography noWrap variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('Folder_Move_CurrentLocation')}: {isLoading
              ? '…'
              : pathLabelOf(tree.byId.get(subject.parentId ?? ROOT_ID) ?? tree.root, rootLabel)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onCancel} sx={dialogCloseStyles} disabled={isMoving}>
          <TimesIcon />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={t('Folder_Move_SearchAll')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setDraft(null);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ opacity: 0.5, fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label={t('Common_Close')} onClick={() => setQuery('')} sx={{ mr: -0.75 }}>
                    <TimesIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Stack direction="row" alignItems="center" sx={navStyles}>
        {searching
          ? (
            <Typography color="text.secondary" sx={{ fontSize: 13, px: 1 }}>
              {t('Folder_Move_Results', { count: nodes.length })}
            </Typography>
          )
          : (
            <>
              <Tooltip title={t('Folder_Move_Up')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t('Folder_Move_Up')}
                    disabled={!cwd.parent}
                    onClick={() => cwd.parent && browse(cwd.parent)}
                    sx={{ mr: 0.5 }}
                  >
                    <ArrowLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {shownCrumbs.map((node, index) => (
                <Fragment key={node?.id ?? 'gap'}>
                  {index > 0 && <Typography aria-hidden color="text.secondary" sx={{ fontSize: 13 }}>›</Typography>}
                  {!node && <Typography color="text.secondary" sx={{ fontSize: 13, px: 0.75 }}>…</Typography>}
                  {node && node === cwd && (
                    <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, px: 0.75, minWidth: 0 }}>{labelOf(node)}</Typography>
                  )}
                  {node && node !== cwd && (
                    <Button size="small" color="inherit" onClick={() => browse(node)} sx={crumbStyles}>
                      {labelOf(node)}
                    </Button>
                  )}
                </Fragment>
              ))}
            </>
          )}
      </Stack>

      <Divider />

      <Box sx={candidateListStyles}>
        {draft !== null && (
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={draftRowStyles}>
            <Box sx={{ display: 'flex', color: 'primary.light', pt: 0.75 }}>
              <FolderIcon style={{ fontSize: 22 }} />
            </Box>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={t('Folder_New_Placeholder')}
              value={draft}
              disabled={isAdding}
              error={Boolean(draftError)}
              helperText={draftError}
              onChange={(e) => {
                setDraft(e.target.value);
                setDraftError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submitDraft();
                }
                // Escape leaves the draft, not the dialog.
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  cancelDraft();
                }
              }}
            />
            <Tooltip title={t('Common_Add')}>
              <span>
                <IconButton size="small" onClick={submitDraft} disabled={isAdding} sx={{ mt: 0.25, color: 'primary.light' }}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('Common_Cancel')}>
              <IconButton size="small" onClick={cancelDraft} sx={{ mt: 0.25 }}>
                <TimesIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {isLoading && (
          <Box sx={emptyStyles}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && nodes.map(renderRow)}

        {!isLoading && nodes.length === 0 && draft === null && (
          <Box sx={emptyStyles}>
            <Typography sx={{ fontSize: 14 }}>
              {searching ? t('Folder_Move_NoResults', [query.trim()]) : t('Folder_Move_NoSubfolders')}
            </Typography>
            {!searching && !moveBlockOf(cwd, subject) && (
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>{t('Folder_Move_NoSubfoldersHint')}</Typography>
            )}
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={dialogFooterStyles}>
        {(ownerChanges || irreversible) && (
          <Alert
            severity={irreversible ? 'error' : 'warning'}
            sx={irreversible ? [{ mb: 1.5 }, irreversibleAlertStyles] : { mb: 1.5 }}
          >
            <AlertTitle sx={{ fontSize: 14, fontWeight: 600 }}>
              {irreversible ? t('Folder_Move_Irreversible_Title') : ownerChangeLine}
            </AlertTitle>
            {irreversible && <Typography sx={{ fontSize: 13 }}>{t('Folder_Move_Irreversible_Body')}</Typography>}
            {irreversible && ownerChanges && <Typography sx={{ fontSize: 13 }}>{ownerChangeLine}</Typography>}
            {ownerChanges && <Typography sx={{ fontSize: 13 }}>{t('Folder_Move_OwnerChange_Body')}</Typography>}
            {sharesLost > 0 && (
              <Typography sx={{ fontSize: 13 }}>{t('Folder_Move_SharesRemoved', { count: sharesLost })}</Typography>
            )}
          </Alert>
        )}

        <Typography noWrap variant="body2" color="text.secondary" sx={{ mb: 1.25, minHeight: 20 }}>
          {!isLoading && (block
            ? t(BLOCK_HINT[block])
            : (
              <>
                {t('Folder_Move_Target')}{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {pathLabelOf(target, rootLabel)}
                </Box>
              </>
            ))}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            startIcon={<PlusIcon />}
            onClick={startDraft}
            disabled={!canCreate || draft !== null || isAdding || isMoving}
            sx={buttonStyles}
          >
            {t('Folder_New')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" size="small" onClick={onCancel} sx={buttonStyles} disabled={isMoving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            color={irreversible ? 'error' : 'primary'}
            onClick={submit}
            sx={buttonStyles}
            disabled={Boolean(block) || isMoving || isLoading}
          >
            {selected ? t('Folder_Move_Submit') : t('Folder_Move_Here')}
          </Button>
        </Stack>
      </Box>
    </>
  );
};

// The theme paints every Alert orange, so the one that cannot be taken back is set apart.
const irreversibleAlertStyles = {
  backgroundColor: (theme: Theme) => alpha(theme.palette.error.dark, 0.45),
  borderColor: (theme: Theme) => alpha(theme.palette.error.light, 0.6),
  '& .MuiAlert-icon': { color: 'error.light' },
};

const navStyles = {
  px: 1.5,
  pt: 1,
  pb: 1,
  minHeight: 48,
  boxSizing: 'border-box',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const crumbStyles = {
  ...buttonStyles,
  minWidth: 0,
  px: 0.75,
  fontWeight: 500,
  fontSize: 13,
  color: 'text.secondary',
  '&:hover': { color: 'text.primary' },
};

const rowStyles = {
  pr: 1.5,
  transition: 'background-color 120ms ease',
  '&:not(:last-of-type)': {
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

const rowButtonStyles = {
  flexGrow: 1,
  minWidth: 0,
  justifyContent: 'flex-start',
  gap: 1.5,
  pl: 2.5,
  pr: 1,
  py: 1.25,
  textAlign: 'left',
  userSelect: 'none',
};

const draftRowStyles = {
  px: 2.5,
  py: 1.25,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const enterStyles = {
  flexShrink: 0,
  color: 'text.secondary',
  '&:hover': { color: 'text.primary' },
};

const reasonStyles = {
  flexShrink: 0,
  fontSize: 12,
  color: 'text.secondary',
};

const locationChipStyles = {
  color: 'text.primary',
  borderColor: 'text.secondary',
};

const markStyles = {
  backgroundColor: 'rgba(250, 204, 21, 0.3)',
  color: 'inherit',
  borderRadius: '2px',
};

const emptyStyles = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  p: 3,
  textAlign: 'center',
};

export default MoveDialog;
